import type { IcicleNode } from '@revelio/replots';

import type { SubsidiaryTreeNode } from '../../../DataProvider/subsidiaries/expand-subsidiaries';
import { subsidiaryDrillChildren } from './bucket-subsidiary-tail';

export const SUBSIDIARY_ROOT_NODE_ID = 'root';

type IndexedSubsidiary = {
  id: string;
  parentId: string | null;
  node: SubsidiaryTreeNode;
};

export type SubsidiaryIndex = Map<string, IndexedSubsidiary>;

export type SubsidiaryViewMeta = {
  name: string;
  total: number;
  drillable: boolean;
};

export type SubsidiaryDrillStep = { id: string; name: string };

const OTHER_ID_SEGMENT = 'other';
/**
 * Other cell labels: spelled out on the first level, an ellipsis deeper in
 * where cells are narrow. The tooltip and breadcrumb keep "N others".
 */
const TOP_OTHER_CELL_LABEL = 'Other';
const NESTED_OTHER_CELL_LABEL = '…';

const childId = (parentId: string, child: SubsidiaryTreeNode) =>
  `${parentId}>${child.isOther === true ? OTHER_ID_SEGMENT : child.rcid}`;

export type RawSubsidiaryIndex = Map<
  number,
  { node: SubsidiaryTreeNode; parent: SubsidiaryTreeNode | null }
>;

/** Raw companies by rcid, each with the parent it hangs off in the tree. */
export function indexRawSubsidiaries(
  root: SubsidiaryTreeNode
): RawSubsidiaryIndex {
  const index: RawSubsidiaryIndex = new Map();
  const visit = (
    node: SubsidiaryTreeNode,
    parent: SubsidiaryTreeNode | null
  ) => {
    if (index.has(node.rcid)) return;
    index.set(node.rcid, { node, parent });
    for (const child of node.children) visit(child, node);
  };
  visit(root, null);
  return index;
}

export function indexSubsidiaryTree(
  root: SubsidiaryTreeNode,
  rootId: string = SUBSIDIARY_ROOT_NODE_ID
): SubsidiaryIndex {
  const index: SubsidiaryIndex = new Map();
  const visit = (
    node: SubsidiaryTreeNode,
    id: string,
    parentId: string | null
  ) => {
    index.set(id, { id, parentId, node });
    for (const child of node.children) visit(child, childId(id, child), id);
  };
  visit(root, rootId, null);
  return index;
}

function promotedRoots(node: SubsidiaryTreeNode, id: string) {
  let parentId = id;
  let roots = subsidiaryDrillChildren(node);
  const skipped: SubsidiaryDrillStep[] = [];
  for (;;) {
    const lone = roots.length === 1 ? roots[0] : undefined;
    if (lone === undefined || lone.children.length === 0) break;
    parentId = childId(parentId, lone);
    skipped.push({ id: parentId, name: lone.name });
    roots = lone.children;
  }
  return { parentId, roots, skipped };
}

export const isSubsidiaryDrillable = (node: SubsidiaryTreeNode, id: string) =>
  node.isOther === true ||
  promotedRoots(node, id).roots.some((root) => root.children.length > 0);

export function subsidiaryDrillSteps(
  index: SubsidiaryIndex,
  id: string
): SubsidiaryDrillStep[] {
  const entry = index.get(id);
  if (entry === undefined) return [];
  return [
    { id, name: entry.node.name },
    ...promotedRoots(entry.node, id).skipped,
  ];
}

export function buildSubsidiaryView(
  index: SubsidiaryIndex,
  viewRootId: string,
  {
    isDrillable = isSubsidiaryDrillable,
  }: {
    isDrillable?: (node: SubsidiaryTreeNode, id: string) => boolean;
  } = {}
): {
  nodes: IcicleNode[];
  meta: Map<string, SubsidiaryViewMeta>;
  skipped: SubsidiaryDrillStep[];
  total: number;
} {
  const viewRoot =
    index.get(viewRootId) ??
    [...index.values()].find((entry) => entry.parentId === null) ??
    null;
  const nodes: IcicleNode[] = [];
  const meta = new Map<string, SubsidiaryViewMeta>();
  if (viewRoot === null) return { nodes, meta, skipped: [], total: 0 };

  const emit = (
    node: SubsidiaryTreeNode,
    id: string,
    parentId: string | null
  ) => {
    const isOther = node.isOther === true;
    const otherFlag = isOther ? { isOther } : {};
    const otherLabel =
      parentId === null ? TOP_OTHER_CELL_LABEL : NESTED_OTHER_CELL_LABEL;
    const name = isOther ? otherLabel : node.name;
    if (node.children.length > 0) {
      const childTotal = node.children.reduce(
        (sum, child) => sum + child.total,
        0
      );
      nodes.push({
        id,
        parentId,
        name,
        ownWeight: Math.max(0, node.total - childTotal),
        ...otherFlag,
      });
    } else {
      nodes.push({
        id,
        parentId,
        name,
        weight: node.total,
        ...otherFlag,
      });
    }
    meta.set(id, {
      name: node.name,
      total: node.total,
      drillable: isDrillable(node, id),
    });
    for (const child of node.children) emit(child, childId(id, child), id);
  };

  const { parentId, roots, skipped } = promotedRoots(
    viewRoot.node,
    viewRoot.id
  );
  for (const root of roots) emit(root, childId(parentId, root), null);

  return {
    nodes,
    meta,
    skipped,
    total: index.get(parentId)?.node.total ?? viewRoot.node.total,
  };
}

export type SubsidiaryTrailStep = { id: string; node: SubsidiaryTreeNode };

/**
 * Every drawn level from the top of the view down to the clicked node, as
 * drill steps. An Other followed by a company stands in for that company's
 * real parent — one of its members — so the trail names the ownership chain
 * rather than the bucket the company happened to be drawn under.
 */
export function subsidiaryClickTrail(
  index: SubsidiaryIndex,
  isDrawn: (id: string) => boolean,
  id: string,
  raw: RawSubsidiaryIndex
): SubsidiaryTrailStep[] {
  const chain: IndexedSubsidiary[] = [];
  let entry = index.get(id);
  while (entry !== undefined && isDrawn(entry.id)) {
    chain.unshift(entry);
    entry = entry.parentId === null ? undefined : index.get(entry.parentId);
  }
  const nodes = chain.map((link) => link.node);
  for (let level = nodes.length - 2; level >= 0; level -= 1) {
    const below = nodes[level + 1];
    if (
      nodes[level]?.isOther !== true ||
      below === undefined ||
      below.isOther === true
    )
      continue;
    const parent = raw.get(below.rcid)?.parent;
    if (parent) nodes[level] = parent;
  }
  let parentId = chain[0]?.parentId ?? id;
  return nodes.map((node) => {
    parentId = childId(parentId, node);
    return { id: parentId, node };
  });
}

export function subsidiaryAncestry(
  raw: RawSubsidiaryIndex,
  rcid: number
): SubsidiaryTrailStep[] {
  const nodes: SubsidiaryTreeNode[] = [];
  const seen = new Set<number>();
  let entry = raw.get(rcid);
  while (entry?.parent != null && !seen.has(entry.node.rcid)) {
    seen.add(entry.node.rcid);
    nodes.unshift(entry.node);
    entry = raw.get(entry.parent.rcid);
  }
  let parentId = SUBSIDIARY_ROOT_NODE_ID;
  return nodes.map((node) => {
    parentId = childId(parentId, node);
    return { id: parentId, node };
  });
}
